import { api, unwrap } from '@saas-kit/lib';

import type { QmsSession, QmsToken, QmsWorkstation } from './types';

/** Per-tenant operating-day marker driving manual reset + local-day numbering. */
export interface QmsDayState {
  timezone: string;
  day_reset_at: string | null;
  reset_by_user_id: string | null;
}

/** Outcome of a manual day reset / fresh start. */
export interface QmsDayResetResult {
  cancelled_tokens: number;
  ended_sessions: number;
  day_reset_at: string;
  timezone: string;
}

/** A waiting token in the live queue overview, annotated with wait age. */
export interface QmsQueueToken {
  id: string;
  display_code: string;
  status: string;
  customer_type_id: string | null;
  customer_name: string | null;
  issued_at: string;
  wait_seconds: number;
}

/** Waiting tokens for one service, in queue order. */
export interface QmsQueueServiceGroup {
  service_id: string;
  service_name: string;
  waiting_count: number;
  tokens: QmsQueueToken[];
}

/** Tenant-wide snapshot of waiting tokens, grouped by service. */
export interface QmsQueueOverview {
  services: QmsQueueServiceGroup[];
  total_waiting: number;
  generated_at: string;
}

export async function issueToken(input: {
  service_id: string;
  customer_type_id?: string | null;
  customer_name?: string | null;
  customer_phone?: string | null;
}): Promise<QmsToken> {
  return unwrap<QmsToken>(api.post('/qms/tokens', input));
}

export async function getMySession(): Promise<QmsSession | null> {
  const data = await unwrap<{ session: QmsSession | null }>(
    api.get('/qms/counters/sessions/me'),
  );
  return data.session;
}

export async function claimCounter(counterId: string): Promise<QmsSession> {
  return unwrap<QmsSession>(api.post(`/qms/counters/${counterId}/claim`));
}

export async function releaseCounter(): Promise<QmsSession> {
  return unwrap<QmsSession>(api.post('/qms/counters/sessions/release'));
}

export async function callNext(): Promise<QmsToken | null> {
  const data = await unwrap<{ token: QmsToken | null }>(
    api.post('/qms/counters/sessions/me/call_next'),
  );
  return data.token;
}

export async function getWorkstation(
  counterId: string,
): Promise<QmsWorkstation> {
  return unwrap<QmsWorkstation>(api.get(`/qms/workstation/${counterId}`));
}

export async function serveToken(tokenId: string): Promise<QmsToken> {
  return unwrap<QmsToken>(api.post(`/qms/tokens/${tokenId}/serve`));
}

export async function completeToken(tokenId: string): Promise<QmsToken> {
  return unwrap<QmsToken>(api.post(`/qms/tokens/${tokenId}/complete`));
}

export async function recallToken(tokenId: string): Promise<QmsToken> {
  return unwrap<QmsToken>(api.post(`/qms/tokens/${tokenId}/recall`));
}

export async function noShowToken(tokenId: string): Promise<QmsToken> {
  return unwrap<QmsToken>(api.post(`/qms/tokens/${tokenId}/no_show`));
}

export async function transferToken(
  tokenId: string,
  targetServiceId: string,
): Promise<QmsToken> {
  return unwrap<QmsToken>(
    api.post(`/qms/tokens/${tokenId}/transfer`, {
      target_service_id: targetServiceId,
    }),
  );
}

export async function sendTokenToCounter(
  tokenId: string,
  targetCounterId: string,
): Promise<QmsToken> {
  return unwrap<QmsToken>(
    api.post(`/qms/tokens/${tokenId}/send_to_counter`, {
      target_counter_id: targetCounterId,
    }),
  );
}

export async function getToken(tokenId: string): Promise<QmsToken> {
  return unwrap<QmsToken>(api.get(`/qms/tokens/${tokenId}`));
}

/** Push a waiting (or already-called) token onto a specific counter — the
 *  reception/supervisor action that does not require a claimed counter. */
export async function forceCallToken(
  tokenId: string,
  targetCounterId: string,
): Promise<QmsToken> {
  return unwrap<QmsToken>(
    api.post(`/qms/tokens/${tokenId}/force_call`, {
      target_counter_id: targetCounterId,
    }),
  );
}

export async function getQueueOverview(): Promise<QmsQueueOverview> {
  return unwrap<QmsQueueOverview>(api.get('/qms/queue'));
}

/** Current operating-day marker (timezone + last reset). */
export async function getDayState(): Promise<QmsDayState> {
  return unwrap<QmsDayState>(api.get('/qms/day'));
}

/** Start a fresh operating day: clear open tokens, end sessions, reset
 *  numbering. Owner/day-reset action. */
export async function resetDay(): Promise<QmsDayResetResult> {
  return unwrap<QmsDayResetResult>(api.post('/qms/day/reset'));
}
